import React, { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import {
  getDisplayPlanningSignals,
  groupPlanningSignals,
  type PlanningSignalGroup,
} from '../../../lib/planning/tags';

interface PlanningSignalsPanelProps {
  application?: Record<string, unknown> | null;
  category?: unknown;
  className?: string;
  initialVisible?: number;
  onOpenDetails?: () => void;
}

const GROUP_LABELS: Record<PlanningSignalGroup, string> = {
  use: 'Use',
  works: 'Works',
  context: 'Context',
  specific: 'Specific',
};

const GROUP_ORDER: PlanningSignalGroup[] = ['use', 'works', 'context', 'specific'];

export default function PlanningSignalsPanel({
  application,
  category,
  className = '',
  initialVisible = 6,
  onOpenDetails,
}: PlanningSignalsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const allSignals = useMemo(
    () => getDisplayPlanningSignals(application ?? undefined, { category, surface: 'modal' }),
    [application, category]
  );

  if (allSignals.length === 0) return null;

  const visibleSignals = expanded ? allSignals : allSignals.slice(0, initialVisible);
  const grouped = groupPlanningSignals(visibleSignals);
  const hiddenCount = Math.max(0, allSignals.length - visibleSignals.length);

  return (
    <section className={`rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-[var(--c-accent)]/20 bg-[var(--c-accent)]/10 text-[var(--c-accent)]">
            <FileText className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight text-[var(--c-text-primary)]">Planning Signals</h3>
            <p className="text-[11px] leading-tight text-[var(--c-text-tertiary)]">
              {allSignals.length} detected trait{allSignals.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {onOpenDetails ? (
            <button
              type="button"
              onClick={onOpenDetails}
              className="min-h-11 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-3 text-[11px] font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/40 hover:text-[var(--c-accent)]"
            >
              Notes
            </button>
          ) : null}
          {hiddenCount > 0 || expanded ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="min-h-11 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-3 text-[11px] font-medium text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/40 hover:text-[var(--c-accent)]"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        {GROUP_ORDER.map((group) => {
          const signals = grouped[group];
          if (!signals.length) return null;
          return (
            <div key={group} className="grid grid-cols-[72px_minmax(0,1fr)] gap-3">
              <div className="pt-1 text-[10px] font-medium uppercase tracking-wide text-[var(--c-text-tertiary)]">
                {GROUP_LABELS[group]}
              </div>
              <div className="flex min-w-0 flex-wrap gap-1.5">
                {signals.map((signal, index) => (
                  <span
                    key={signal.slug}
                    className="inline-flex max-w-full items-center gap-1 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1.5 text-[11px] font-medium leading-none text-[var(--c-text-primary)]"
                    title={signal.label}
                  >
                    {index === 0 ? (
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--c-accent)]" />
                    ) : null}
                    <span className="min-w-0 truncate">{signal.label}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
